import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Send, DollarSign, TrendingUp, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { useTheme } from "@/components/ThemeContext";

/**
 * Two-sided proposal dashboard.
 * - mode="admin": edit services for a specific client (create / update / delete)
 * - mode="client": read services scoped to current user, toggle on/off with live totals + submit
 */
export function ProposalPanel({ mode = "client", clientId = null, clientName = "" }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = mode === "admin" && clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
      const res = await api.get(`/proposals/services${params}`);
      setServices(res.data || []);
      if (mode === "client") {
        setSelected(new Set((res.data || []).map((s) => s.id)));
      }
    } catch { setServices([]); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, mode]);

  const total = useMemo(
    () => services.reduce((sum, s) => sum + (selected.has(s.id) ? Number(s.price || 0) : 0), 0),
    [services, selected]
  );
  const cutServices = useMemo(
    () => services.filter((s) => !s.is_required && !selected.has(s.id)),
    [services, selected]
  );

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/proposals/submit", {
        selected_service_ids: Array.from(selected),
        notes,
      });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 6000);
    } finally { setSubmitting(false); }
  };

  // Palette tokens by theme
  const surface = isDark ? "bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl" : "bg-white border border-slate-200 shadow-sm";
  const textMain = isDark ? "text-slate-100" : "text-slate-900";
  const textMuted = isDark ? "text-slate-400" : "text-slate-500";

  if (mode === "admin") {
    return (
      <AdminProposalEditor
        services={services}
        clientId={clientId}
        clientName={clientName}
        onChange={load}
        surface={surface}
        textMain={textMain}
        textMuted={textMuted}
        isDark={isDark}
      />
    );
  }

  // ============ CLIENT VIEW ============
  return (
    <div className="space-y-4" data-testid="proposal-client-panel">
      <div className={`rounded-2xl p-6 ${surface}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className={`font-mono text-[10px] uppercase tracking-[0.28em] ${isDark ? "text-[#FF3355]" : "text-red-600"}`}>// YOUR CUSTOM PROPOSAL</div>
            <h2 className={`mt-2 font-display text-2xl sm:text-3xl font-bold ${textMain}`}>Build your engagement.</h2>
            <p className={`mt-2 text-sm ${textMuted}`}>Toggle each service on or off — see exactly what you gain (and what you lose) in real time.</p>
          </div>
          <div className={`rounded-2xl px-5 py-4 ${isDark ? "bg-slate-950 border border-[#FF3355]/50" : "bg-red-50 border border-red-300"}`}>
            <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${isDark ? "text-[#FF3355]" : "text-red-600"}`}>// LIVE TOTAL</div>
            <div className={`mt-1 font-display text-3xl font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`} data-testid="proposal-live-total">
              ${total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
            </div>
            <div className={`mt-1 font-mono text-[9px] uppercase tracking-[0.22em] ${textMuted}`}>{selected.size} of {services.length} selected</div>
          </div>
        </div>
      </div>

      {loading && <div className={`text-sm ${textMuted}`}>Loading services…</div>}
      {!loading && services.length === 0 && (
        <div className={`rounded-2xl p-8 text-center ${surface}`}>
          <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${textMuted}`}>// NO PROPOSAL YET</div>
          <p className={`mt-2 text-sm ${textMuted}`}>Your account manager hasn&apos;t published a proposal yet. Check back shortly.</p>
        </div>
      )}

      <div className="grid gap-3">
        {services.map((s) => {
          const on = selected.has(s.id);
          const forced = s.is_required;
          return (
            <div
              key={s.id}
              data-testid={`proposal-service-${s.id}`}
              className={`rounded-2xl p-5 transition-all ${surface} ${
                on
                  ? isDark
                    ? "border-[#FF3355]/60 shadow-[0_0_30px_-15px_rgba(255,51,85,0.5)]"
                    : "border-red-400 shadow-[0_2px_20px_-5px_rgba(255,51,85,0.2)]"
                  : isDark
                  ? "opacity-60"
                  : "opacity-80"
              }`}
            >
              <div className="flex items-start justify-between gap-5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-display text-lg font-bold ${textMain}`}>{s.name}</h3>
                    {forced && (
                      <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] ${isDark ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-400" : "bg-emerald-50 border border-emerald-300 text-emerald-700"}`}>Required</span>
                    )}
                  </div>
                  {s.description && <p className={`mt-2 text-sm leading-relaxed ${textMuted}`}>{s.description}</p>}
                  {s.projected_roi && (
                    <div className={`mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] ${isDark ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-400" : "bg-emerald-50 border border-emerald-300 text-emerald-700"}`}>
                      <TrendingUp size={11} /> ROI · {s.projected_roi}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-3 shrink-0">
                  <div className={`font-display text-2xl font-bold ${textMain}`}>
                    ${Number(s.price || 0).toLocaleString()}
                  </div>
                  <button
                    type="button"
                    disabled={forced}
                    onClick={() => {
                      setSelected((prev) => {
                        const n = new Set(prev);
                        if (n.has(s.id)) n.delete(s.id); else n.add(s.id);
                        return n;
                      });
                    }}
                    data-testid={`proposal-toggle-${s.id}`}
                    aria-pressed={on}
                    className={`relative inline-flex h-8 w-16 shrink-0 items-center rounded-full transition-colors ${
                      on ? (isDark ? "bg-[#FF3355]" : "bg-red-500") : (isDark ? "bg-slate-700" : "bg-slate-300")
                    } ${forced ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow ${on ? "translate-x-9" : "translate-x-1"}`} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* What you lose panel */}
      {cutServices.length > 0 && (
        <div className={`rounded-2xl p-5 ${isDark ? "bg-red-950/20 border border-red-900/50" : "bg-red-50 border border-red-200"}`} data-testid="proposal-lose-panel">
          <div className="flex items-start gap-3">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${isDark ? "bg-red-950/50 border border-red-500/40" : "bg-red-100 border border-red-300"}`}>
              <AlertTriangle size={16} className={isDark ? "text-red-400" : "text-red-600"} />
            </div>
            <div className="flex-1">
              <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${isDark ? "text-red-400" : "text-red-600"}`}>// WHAT YOU LOSE IF YOU CUT THESE</div>
              <ul className="mt-3 space-y-1.5">
                {cutServices.map((s) => (
                  <li key={s.id} className={`text-[13px] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    <span className="font-semibold">{s.name}</span>
                    {s.projected_roi ? <span className={isDark ? "text-slate-400" : "text-slate-500"}> — you forfeit: <span className={isDark ? "text-red-400" : "text-red-600"}>{s.projected_roi}</span></span> : null}
                    <span className={isDark ? "text-slate-500" : "text-slate-400"}> · saves ${Number(s.price || 0).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {services.length > 0 && (
        <div className={`rounded-2xl p-5 ${surface}`}>
          <label className={`block font-mono text-[10px] uppercase tracking-[0.22em] mb-2 ${isDark ? "text-[#FF3355]" : "text-red-600"}`}>// NOTES FOR YOUR ACCOUNT MANAGER</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Any timing constraints, priorities, or questions before we lock this in..."
            data-testid="proposal-notes"
            className={`w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors ${
              isDark
                ? "bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 focus:border-[#FF3355]"
                : "bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-red-500"
            }`}
          />
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="proposal-submit"
            className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] font-bold transition-all disabled:opacity-50 ${
              isDark
                ? "bg-[#FF3355] text-white hover:bg-[#ff4d6a] shadow-[0_0_30px_-8px_rgba(255,51,85,0.6)]"
                : "bg-red-600 text-white hover:bg-red-500 shadow-[0_2px_10px_-2px_rgba(220,38,38,0.4)]"
            }`}
          >
            <Send size={13} /> {submitting ? "Sending…" : submitted ? "Sent ✓ · we&apos;ll reach out shortly" : `Submit selections · $${total.toLocaleString()}`}
          </button>
        </div>
      )}
    </div>
  );
}

// ============ ADMIN EDITOR ============
function AdminProposalEditor({ services, clientId, clientName, onChange, surface, textMain, textMuted, isDark }) {
  const [form, setForm] = useState({ name: "", price: "", description: "", projected_roi: "", is_required: false });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId || !form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/proposals/services", {
        client_id: clientId,
        name: form.name.trim(),
        description: form.description.trim(),
        price: Number(form.price || 0),
        projected_roi: form.projected_roi.trim(),
        is_required: !!form.is_required,
      });
      setForm({ name: "", price: "", description: "", projected_roi: "", is_required: false });
      await onChange();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/proposals/services/${id}`);
    await onChange();
  };

  const total = services.reduce((sum, s) => sum + Number(s.price || 0), 0);

  if (!clientId) {
    return <div className={`rounded-2xl p-8 text-center ${surface}`}><p className={`text-sm ${textMuted}`}>Select a client to build a proposal.</p></div>;
  }

  return (
    <div className="space-y-4" data-testid="proposal-admin-panel">
      <div className={`rounded-2xl p-6 ${surface}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className={`font-mono text-[10px] uppercase tracking-[0.28em] ${isDark ? "text-[#FF3355]" : "text-red-600"}`}>// PROPOSAL EDITOR</div>
            <h3 className={`mt-2 font-display text-xl font-bold ${textMain}`}>Building for: {clientName || clientId}</h3>
          </div>
          <div className={`rounded-lg px-4 py-2 ${isDark ? "bg-slate-950 border border-slate-800" : "bg-slate-50 border border-slate-200"}`}>
            <span className={`font-mono text-[10px] uppercase tracking-[0.22em] ${textMuted}`}>Full package: </span>
            <span className={`font-display text-lg font-bold ${textMain}`}>${total.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Add form */}
      <form onSubmit={submit} className={`rounded-2xl p-5 space-y-3 ${surface}`}>
        <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${isDark ? "text-[#FF3355]" : "text-red-600"}`}>// ADD SERVICE</div>
        <div className="grid md:grid-cols-2 gap-3">
          <input
            required
            data-testid="proposal-admin-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Service name (e.g. Landing Page Build)"
            className={`w-full rounded-lg px-3 py-2 text-sm outline-none ${isDark ? "bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 focus:border-[#FF3355]" : "bg-slate-50 border border-slate-300 focus:border-red-500"}`}
          />
          <input
            required
            type="number"
            step="0.01"
            min="0"
            data-testid="proposal-admin-price"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            placeholder="Price (USD)"
            className={`w-full rounded-lg px-3 py-2 text-sm outline-none ${isDark ? "bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 focus:border-[#FF3355]" : "bg-slate-50 border border-slate-300 focus:border-red-500"}`}
          />
        </div>
        <textarea
          data-testid="proposal-admin-desc"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={2}
          placeholder="Description — what the client gets"
          className={`w-full rounded-lg px-3 py-2 text-sm outline-none ${isDark ? "bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 focus:border-[#FF3355]" : "bg-slate-50 border border-slate-300 focus:border-red-500"}`}
        />
        <input
          data-testid="proposal-admin-roi"
          value={form.projected_roi}
          onChange={(e) => setForm({ ...form, projected_roi: e.target.value })}
          placeholder="Projected ROI (e.g. '3x pipeline in 90 days')"
          className={`w-full rounded-lg px-3 py-2 text-sm outline-none ${isDark ? "bg-slate-950 border border-slate-800 text-slate-100 placeholder-slate-600 focus:border-[#FF3355]" : "bg-slate-50 border border-slate-300 focus:border-red-500"}`}
        />
        <div className="flex items-center justify-between">
          <label className={`inline-flex items-center gap-2 text-sm ${textMuted} cursor-pointer`}>
            <input type="checkbox" checked={form.is_required} onChange={(e) => setForm({ ...form, is_required: e.target.checked })} className="rounded" />
            Required (client cannot toggle off)
          </label>
          <button
            type="submit"
            disabled={saving}
            data-testid="proposal-admin-add"
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] font-bold ${isDark ? "bg-[#FF3355] text-white hover:bg-[#ff4d6a]" : "bg-red-600 text-white hover:bg-red-500"} disabled:opacity-50`}
          >
            <Plus size={12} /> {saving ? "Adding…" : "Add Service"}
          </button>
        </div>
      </form>

      {/* Existing services */}
      <div className="grid gap-3">
        {services.map((s) => (
          <div key={s.id} className={`rounded-2xl p-5 ${surface}`} data-testid={`proposal-admin-item-${s.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className={`font-display text-base font-bold ${textMain}`}>{s.name}</h4>
                  {s.is_required && <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] ${isDark ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-400" : "bg-emerald-50 border border-emerald-300 text-emerald-700"}`}>Required</span>}
                </div>
                {s.description && <p className={`mt-1 text-[13px] ${textMuted}`}>{s.description}</p>}
                {s.projected_roi && <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] ${isDark ? "text-emerald-400" : "text-emerald-700"}`}><TrendingUp size={10} /> {s.projected_roi}</div>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`font-display text-lg font-bold ${textMain}`}>${Number(s.price || 0).toLocaleString()}</span>
                <button onClick={() => remove(s.id)} data-testid={`proposal-admin-delete-${s.id}`} className={`p-1.5 rounded-lg transition-colors ${isDark ? "text-slate-500 hover:text-red-400 hover:bg-slate-800" : "text-slate-400 hover:text-red-600 hover:bg-red-50"}`} aria-label="Delete service">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
